import { useEffect, useState } from 'react'
import { CJMDetailsDto, CJMFiltersDto } from '@/api/Metrics/Types/CJMDto'
import { Loading } from '@/components/Loading'
import { CJMFiltersForm } from '@/components/Metrics/components/CJMFiltersForm'
import { processCJMWidgetKey } from '@/components/Metrics/helpers/processCJMWidgetKey'
import { EmptyStateSkeleton } from '@/components/ui/empty-state-skeleton'
import { cn } from '@/lib/utils'
import { useAppSelector } from '@/store'
import { useApiClient } from '@/utils/api/ApiClient'
import { MetricsApiClient } from '@/utils/api/metricsApi'
import { useCallbackWithForceRefresh } from '@/utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from '@/utils/api/useFetch'
import { Filter } from 'lucide-react'

import { WidgetCard } from './components/WidgetCard'

interface ModalProps {
  data: CJMFiltersDto | undefined
  visible: boolean
}

const initialModalState = { data: undefined, visible: false }
const initialFiltersState: CJMFiltersDto = {
  ibCountryIds: [],
  clientCountryIds: [],
  hasMainIb: false,
}

const useCJMWidgetModal = () => {
  const [CJMWidgetModal, setCJMWidgetModal] = useState<ModalProps>(initialModalState)

  const handleCJMWidgetModalOpen = (data?: CJMFiltersDto) => {
    setCJMWidgetModal({ data: data, visible: true })
  }

  const handleCJMWidgetModalClose = () => {
    setCJMWidgetModal(initialModalState)
  }

  return {
    CJMWidgetModal,
    handleCJMWidgetModalOpen,
    handleCJMWidgetModalClose,
  }
}

export const CJMWidget = () => {
  const metricsApiClient = useApiClient(MetricsApiClient)
  const countries = useAppSelector((state) => state.identity.countries) || []
  const { CJMWidgetModal, handleCJMWidgetModalOpen, handleCJMWidgetModalClose } =
    useCJMWidgetModal()

  const [filters, setFilters] = useState<CJMFiltersDto>(initialFiltersState)

  const { callback: fetchData } = useCallbackWithForceRefresh(() => {
    return metricsApiClient.getCJMMetrics(filters)
  }, [filters])

  const { data, isLoading } = useFetchOne(fetchData)

  const applyFilters = (filters: CJMFiltersDto) => {
    setFilters(filters)
    localStorage.setItem('cjm_filters', JSON.stringify(filters))
    handleCJMWidgetModalClose()
  }

  const resetFilters = () => {
    setFilters(initialFiltersState)
    localStorage.removeItem('cjm_filters')
    handleCJMWidgetModalClose()
  }

  useEffect(() => {
    const savedFilters = localStorage.getItem('cjm_filters')

    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters)
        setFilters(parsedFilters)
      } catch (error) {
        console.error(error)
      }
    }
  }, [])

  const cjmEntries = data ? Object.entries(data) : []
  const hasData = cjmEntries.some(([, { value }]) => !!value)
  const isFiltered =
    filters &&
    (filters.ibCountryIds?.length || filters.clientCountryIds?.length || filters.hasMainIb)

  return (
    <div className='flex flex-col justify-evenly gap-3 p-3 h-full overflow-auto'>
      <div className='flex items-center justify-between'>
        <div className='text-[1.125rem] font-semibold text-muted-foreground'>
          Clients activation tracking
        </div>
        <div
          style={{ pointerEvents: CJMWidgetModal.visible ? 'none' : 'auto' }}
          className='inline-block'
        >
          <button
            onClick={() => handleCJMWidgetModalOpen(filters)}
            className={cn(
              'flex items-center gap-2 rounded-lg border px-2 py-1 text-sm font-medium transition-all',
              isFiltered
                ? 'border-purple-500 bg-purple-50 text-purple-700 hover:bg-purple-100'
                : 'border-gray-300  hover:border-gray-400 hover:bg-gray-50'
            )}
          >
            <Filter className='h-3 w-3' />
            <span>Filters</span>
            {isFiltered && (
              <span className='flex min-w-[24px] items-center justify-center rounded-full bg-purple-600 px-2 py-0.5 text-xs font-semibold text-white'>
                {(filters.ibCountryIds?.length || 0) +
                  (filters.clientCountryIds?.length || 0) +
                  (filters.hasMainIb ? 1 : 0)}
              </span>
            )}
          </button>
        </div>
      </div>
      <CJMFiltersForm
        isOpen={CJMWidgetModal.visible}
        onClose={handleCJMWidgetModalClose}
        countries={countries}
        data={CJMWidgetModal.data}
        onSubmit={(data) => {
          applyFilters(data as CJMFiltersDto)
        }}
        resetFilters={resetFilters}
      />
      <Loading isLoading={isLoading} modalView fullWidth={false}>
        {hasData ? (
          <div className='grid grid-cols-[repeat(auto-fill,minmax(165px,1fr))] auto-rows-[1fr] gap-2 w-full'>
            {cjmEntries.map(([key, value]) => {
              if (key === 'linkClicked') {
                return <WidgetCard key={key} title='Total link clicks' value={value} />
              }

              const details = value as CJMDetailsDto

              return (
                <WidgetCard
                  key={key}
                  title={processCJMWidgetKey(key)}
                  value={details.value}
                  conversionRate={
                    details.value && details.conversionRate ? details.conversionRate : undefined
                  }
                />
              )
            })}
          </div>
        ) : (
          <div className='flex flex-col h-full min-h-0 p-3 rounded-lg'>
            <div className='flex flex-col flex-1 min-h-0'>
              <EmptyStateSkeleton title='No data to display' className='h-full' />
            </div>
          </div>
        )}
      </Loading>
    </div>
  )
}
